import { useEffect, useState, Fragment } from "react";
import { toast } from "sonner";
import { UserPlus, Search, KeyRound } from "lucide-react";
import { Panel, Btn, Field, Input } from "@/components/kit";
import api, { formatApiError } from "@/lib/api";

export function GuestCreate() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [guests, setGuests] = useState([]);
  const [q, setQ] = useState("");
  const [resetId, setResetId] = useState(null);
  const [pw, setPw] = useState("");

  const load = () => api.get("/guests").then((r) => setGuests(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/guests", form);
      toast.success(`Guest account created for ${r.data.user.name}`);
      setForm({ name: "", email: "", phone: "", password: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  const doReset = async (g) => {
    if (pw.length < 6) return toast.error("Password must be at least 6 characters");
    try {
      await api.post(`/guests/${g.id}/reset-password`, { password: pw });
      toast.success(`Password reset for ${g.name}`);
      setResetId(null); setPw("");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const filtered = guests.filter((g) => `${g.name} ${g.email} ${g.phone || ""}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="grid lg:grid-cols-5 gap-6">
      <div className="lg:col-span-2">
        <Panel className="p-6">
          <h3 className="font-head font-bold text-white mb-1 flex items-center gap-2"><UserPlus size={18} /> Create a guest account</h3>
          <p className="text-sm text-purple-300/70 mb-4">Set up a login so a guest can use the Customer portal to request reservations and raise service requests. Share the temporary password — they can change it later.</p>
          <form onSubmit={submit} className="space-y-3" data-testid="guest-create-form">
            <Field label="Guest name"><Input data-testid="guest-name" required value={form.name} onChange={set("name")} placeholder="Grace Guest" /></Field>
            <Field label="Email"><Input data-testid="guest-email" type="email" required value={form.email} onChange={set("email")} placeholder="guest@example.com" /></Field>
            <Field label="Phone (optional)"><Input data-testid="guest-phone" value={form.phone} onChange={set("phone")} placeholder="+1 555 0100" /></Field>
            <Field label="Temporary password"><Input data-testid="guest-password" type="text" required value={form.password} onChange={set("password")} placeholder="At least 6 characters" /></Field>
            <Btn type="submit" disabled={busy} data-testid="guest-create-submit">{busy ? "Creating…" : "Create guest account"}</Btn>
          </form>
        </Panel>
      </div>

      <div className="lg:col-span-3 space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h3 className="font-head font-bold text-white">Guest accounts <span className="text-sm text-purple-400 font-normal">({guests.length})</span></h3>
          <div className="relative w-64 max-w-full">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-purple-400" />
            <input data-testid="guest-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search guests…"
              className="w-full bg-[#120429] border border-[#361368] rounded-xl pl-9 pr-4 py-2.5 text-white placeholder-purple-400/40 focus:outline-none focus:border-[#FF2A6D] text-sm" />
          </div>
        </div>
        {!filtered.length ? (
          <Panel className="p-10 text-center text-purple-400/70">{guests.length ? "No guests match your search." : "No guest accounts yet."}</Panel>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-[#2E1256] bg-[#14052E]">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs uppercase tracking-wider text-purple-300 bg-[#1F0842]">{["Name", "Email", "Phone", "Action"].map((h) => <th key={h} className="p-3">{h}</th>)}</tr></thead>
              <tbody>
                {filtered.map((g) => (
                  <Fragment key={g.id}>
                    <tr className="border-t border-[#230948] hover:bg-[#1E0A3E]" data-testid={`guest-row-${g.id}`}>
                      <td className="p-3 text-purple-100 font-medium">{g.name}</td>
                      <td className="p-3 text-purple-300">{g.email}</td>
                      <td className="p-3 text-purple-300">{g.phone || "—"}</td>
                      <td className="p-3"><Btn variant="outline" data-testid={`guest-reset-${g.id}`} onClick={() => { setResetId(resetId === g.id ? null : g.id); setPw(""); }}><KeyRound size={13} /> Reset password</Btn></td>
                    </tr>
                    {resetId === g.id && (
                      <tr className="bg-[#1A0736]"><td colSpan={4} className="p-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="w-full sm:w-72"><Input data-testid={`guest-reset-input-${g.id}`} type="text" placeholder="New password (min 6 characters)" value={pw} onChange={(e) => setPw(e.target.value)} /></div>
                          <Btn data-testid={`guest-reset-save-${g.id}`} onClick={() => doReset(g)}>Save new password</Btn>
                          <Btn variant="ghost" onClick={() => { setResetId(null); setPw(""); }}>Cancel</Btn>
                        </div>
                      </td></tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
